import { IAnyType } from 'common/types/common';

type BreadcrumbItem = {
  name: string;
  url: string;
};

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, ' ').trim();

const stripHtml = (value: string) =>
  normalizeWhitespace(value.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, ' '));

const extractNodeText = (node: IAnyType): string => {
  if (!node) {
    return '';
  }

  if (typeof node === 'string') {
    return stripHtml(node);
  }

  if (Array.isArray(node)) {
    return normalizeWhitespace(node.map(extractNodeText).filter(Boolean).join(' '));
  }

  if (typeof node !== 'object') {
    return '';
  }

  if (typeof node.value === 'string') {
    const ownText = stripHtml(node.value);
    if (node.content) {
      return normalizeWhitespace(
        [ownText, extractNodeText(node.content)].filter(Boolean).join(' '),
      );
    }

    return ownText;
  }

  if (node.content) {
    return extractNodeText(node.content);
  }

  return '';
};

export const buildBreadcrumbListSchema = (items: BreadcrumbItem[]) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const buildFaqPageSchema = (
  faqItems: Array<{ title?: string; value?: string; content?: IAnyType[] }>,
) => {
  const mainEntity = faqItems
    .map(item => {
      const question = stripHtml(item.title || '');
      const answer = item.value ? stripHtml(item.value) : extractNodeText(item.content || []);

      if (!question || !answer) {
        return null;
      }

      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      };
    })
    .filter(Boolean);

  if (!mainEntity.length) {
    return null;
  }

  return {
    '@type': 'FAQPage',
    mainEntity,
  };
};

export const extractFaqSchemaFromBlogContent = (content: IAnyType[] = []) => {
  const faqSchemas = content
    .filter(item => item?.type === 'faq' && Array.isArray(item.content))
    .map(item => buildFaqPageSchema(item.content))
    .filter(Boolean);

  return faqSchemas;
};
